import VirtualList from "@/components/VirtualList";
import useDeepComparison from "@/hooks/useDeepComparison";
import { InputFieldType, formFieldError } from "@/types";
import customMatchSort from "@/utils/customMatch";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import {
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteOwnerState,
  AutocompleteRenderGetTagProps,
  AutocompleteRenderInputParams,
  Box,
  Chip,
  ChipProps,
  CircularProgress,
  FilterOptionsState,
  OutlinedInput,
  Popper,
  PopperProps,
  Tooltip,
  Typography,
  autocompleteClasses,
  createFilterOptions,
} from "@mui/material";
import * as React from "react";
import {
  FieldValues,
  UseControllerProps,
  useController,
  useWatch,
} from "react-hook-form";
import CustomFormControl from "../../FormControl/CustomFormControl";
import { ExtraButtonsWithInputLabel } from "../inputfields.interface";
import { autocompleteValue } from "./autocompleteValue";

/**
 * The house default for every dropdown in the app, in one object so changing it
 * is a one-line edit rather than a sweep. Any field can override any of these
 * individually via props; a prop always wins, and anything not passed falls back
 * here. That is the whole contract: change this to move everything, pass a prop
 * to move one.
 *
 * Should this ever become a user-facing preference, this object is what a stored
 * setting would replace — nothing else needs to know.
 */
export const AUTOCOMPLETE_MENU_DEFAULTS = {
  /** Wrap rather than cut, because cutting a value you are choosing between is
   *  worse than a tall option. */
  optionDisplay: "clamp" as const,
  /** Four, not two: at the flexible width almost nothing reaches even two
   *  lines, so this is headroom for the pathological name rather than a routine
   *  trim. */
  optionMaxLines: 4,
  /** Size to content instead of matching the field. */
  dropdownWidth: "flexible" as const,
  /** Floor and ceiling in px. GitLab's dropdown bounds (15.5rem / 28.5rem). */
  dropdownMinWidth: 248,
  dropdownMaxWidth: 456,
};

/** Hidden values a collapsed field's "+N" tooltip names before "and N more". */
const HIDDEN_TAGS_TOOLTIP_MAX = 10;

export default function CustomAutocomplete<T extends FieldValues>({
  label = null,
  control,
  itemList: options = [],
  name,
  required,
  loading = false,
  error,
  type = "primary",
  disabled = false,
  placeholder,
  valueContainsLabel = false,
  isMultiple = false,
  onSearch,
  onChange,
  readOnly = false,
  disabledValues = [],
  inputType = "text",
  allowCustomValues = false,
  customMatcher = false,
  toolTip = null,
  virtualize = false,
  disableClearable = false,
  limitTags,
  keepTagsCollapsed = false,
  stringToRenderBeforeValue,
  noMenuAllowed,
  customIsOptionEqualToValue,
  isCustomList = false,
  renderTags,
  renderOptionAdornment,
  getTagChipProps,
  listboxProps,
  customList,
  extraLabelButtons,
  isWatch = false,
  isRetainInputValue = false,
  disableClientFilter = false,
  optionDisplay = AUTOCOMPLETE_MENU_DEFAULTS.optionDisplay,
  optionMaxLines = AUTOCOMPLETE_MENU_DEFAULTS.optionMaxLines,
  dropdownWidth = AUTOCOMPLETE_MENU_DEFAULTS.dropdownWidth,
  dropdownMinWidth = AUTOCOMPLETE_MENU_DEFAULTS.dropdownMinWidth,
  dropdownMaxWidth = AUTOCOMPLETE_MENU_DEFAULTS.dropdownMaxWidth,
}: CustomAutocompleteProps<T>) {
  const [inputValue, setInputValue] = React.useState("");
  const isChanged = useDeepComparison(options);

  const values = React.useMemo(
    () => options.map((o: any) => o.value),
    [isChanged]
  );

  const valueKeyMapping = React.useMemo(() => {
    const obj: any = {};
    options.forEach((item: any) => (obj[item.value] = item.label));
    return obj;
  }, [values]);

  function getLabel(value: any) {
    if (valueContainsLabel) {
      return value.label;
    } else if (value) {
      if (valueKeyMapping[value]) {
        return valueKeyMapping[value];
      }
      return "";
    } else {
      return "";
    }
  }

  const secondaryStyles =
    type === "secondary"
      ? {
          backgroundColor: "background.default",
        }
      : {};

  const { field } = useController({ control, name });
  const watchedValue = useWatch({
    name,
    control,
    disabled: !isWatch,
  });

  React.useEffect(() => {
    if (isWatch) {
      if (field.value !== watchedValue) {
        field.onChange(watchedValue);
      }
    }
  }, [watchedValue]);

  const handleOnChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: any,
    reason: AutocompleteChangeReason
  ) => {
    const inputValue = (event.target as HTMLInputElement).value ?? null;
    let requiredValue = value;
    if (!isMultiple && isRetainInputValue) {
      setInputValue(valueKeyMapping[value]);
    }
    if ((event as React.KeyboardEvent<Element>)["code"] === "Enter") {
      if (allowCustomValues && valueContainsLabel) {
        const customValue = {
          label: inputValue,
          value: inputValue,
        };
        if (isMultiple) {
          const filteredValue = value.filter((x: unknown) => x !== inputValue);
          if (isRetainInputValue) {
            setInputValue("");
          }
          //checking if the value already exists
          if (
            typeof customValue === "object" &&
            !filteredValue.some((x: any) => x.value === customValue.value)
          ) {
            requiredValue = [...filteredValue, customValue];
          } else {
            requiredValue = filteredValue;
          }
        } else {
          requiredValue = customValue;
        }
      }
    }

    field.onChange(requiredValue);
    if (onChange) onChange(requiredValue, reason);
  };

  const handleOnInputChange: React.FormEventHandler<HTMLDivElement> = (
    event: React.FormEvent<HTMLInputElement>
  ) => {
    const result = (event.target as HTMLInputElement).value;
    if (isRetainInputValue) {
      setInputValue(result);
    }
    if (onSearch) onSearch(result, name);
  };

  const filterOptions = (
    options: any[],
    { inputValue }: FilterOptionsState<any>
  ) => {
    return customMatchSort(options, inputValue);
  };

  const isValueSelected = (option: any, value: any) => {
    return Boolean(option.value === value.value);
  };

  // Spread onto <Autocomplete /> last, so a caller's renderTags and the virtualize
  // branch's renderOption both override the defaults set further down.
  const dynamicProps: Record<string, any> = {};
  if (!virtualize && isCustomList) {
    dynamicProps["ListboxComponent"] = customList;
    dynamicProps["ListboxProps"] = listboxProps;
  }
  if (renderTags) {
    dynamicProps["renderTags"] = renderTags;
  }
  if (virtualize) {
    dynamicProps["renderOption"] = (props: object, option: any, state: any) =>
      [
        {
          props,
          option,
          state: state.index,
          label: valueKeyMapping[option],
        },
      ] as React.ReactNode;
    dynamicProps["ListboxComponent"] = VirtualList;
  }
  if (valueContainsLabel) {
    dynamicProps["isOptionEqualToValue"] = isValueSelected;
  }
  if (noMenuAllowed != undefined) dynamicProps["open"] = noMenuAllowed;
  // `keepTagsCollapsed` moves the collapse into `renderBoundedTags`, which does
  // it whether or not the field has focus. MUI's own `limitTags` must then stay
  // at its default of -1: it counts what `renderTags` returns, so it would read
  // the "+N" there as one more tag and replace it with a "+1" of its own.
  const collapseTagsAt =
    isMultiple && keepTagsCollapsed && limitTags !== undefined && limitTags >= 0
      ? limitTags
      : null;
  // Only meaningful on a multi-select, and only set when a caller asks for it —
  // MUI's own default is -1 (show every chip), which is what every existing
  // consumer already gets.
  if (isMultiple && limitTags !== undefined && collapseTagsAt === null)
    dynamicProps["limitTags"] = limitTags;
  if (customIsOptionEqualToValue)
    dynamicProps["isOptionEqualToValue"] = customIsOptionEqualToValue;
  const dynamicInputProps = (
    params: AutocompleteRenderInputParams["inputProps"]
  ) => {
    return stringToRenderBeforeValue
      ? {
          value: `${stringToRenderBeforeValue}: ${params.value}`,
        }
      : {};
  };

  if (isRetainInputValue) {
    dynamicProps["inputValue"] = inputValue;
  }

  // Renders one option's label under whichever treatment `optionDisplay` names:
  // one line with an ellipsis, free wrapping, or wrapping capped at
  // `optionMaxLines`. MUI's own option wraps without a cap, which is what turned
  // a long campaign name into a three-line row.
  //
  // Carries no `title`. Whatever is clipped is revealed by the app-level
  // <OverflowTooltip />, which detects `-webkit-line-clamp` as well as
  // `text-overflow: ellipsis` — so it covers the clamped option too, and a
  // native title tooltip here would simply open a second one on top of it.
  //
  // The <li> keeps MUI's own padding. Moving it onto the label to close
  // OverflowTooltip's hover dead band — which is real: the label is a
  // *descendant*, and the walk only goes up — was the wrong place to fix it, in
  // two ways. Padding on a `-webkit-line-clamp` box puts the `overflow: hidden`
  // clip edge at the padding box, so the top of line five painted into the
  // bottom gutter under the ellipsis; and `padding: 0` does nothing about the
  // `min-height: 48px` MUI applies below the `sm` breakpoint, so under 600px the
  // label was 36px in a 48px row and the band came straight back.
  //
  // `measure()` in <OverflowTooltip /> looks at the hovered element's single
  // child instead, which fixes every padded-container-plus-clipped-label in the
  // app at once — this one and ControlledAutoComplete, for two.
  //
  // Skipped for `virtualize` (VirtualList consumes a data tuple from renderOption,
  // not JSX) and for `isCustomList` (CustomListForAutocomplete reads the element's
  // key as the label and `data-option-index` off the option props to route its
  // include/exclude buttons; it discards children, so this would only add risk).
  const renderOptionLabel = (
    props: React.HTMLAttributes<HTMLLIElement> & { key?: string },
    option: any
  ) => {
    const { key: _muiKey, ...optionProps } = props;
    const optionLabel = getLabel(option);
    // Keyed by position, which is the only thing here guaranteed unique.
    //
    // Not the label: two records can share a display name — two advertisers
    // both called "Acme" — and `getLabel` returns "" for anything missing from
    // `valueKeyMapping`, so several options collapse to "". Not the value
    // either: lists carry a placeholder `{ label: "Select", value: "" }`, and a
    // join can repeat an id. A duplicate key makes React reconcile two <li>s
    // onto one node, and the click then commits the wrong record, because MUI
    // routes it through the `data-option-index` on whichever element survived.
    //
    // MUI's own `key` is the label, so it is dropped rather than used. Index
    // keys are safe here precisely because that is already how MUI identifies an
    // option — these <li>s hold no state of their own to be reattached wrongly.
    const optionIndex = (optionProps as { "data-option-index"?: number })[
      "data-option-index"
    ];
    const adornment = renderOptionAdornment?.(option) ?? null;
    const text = (
      <Typography
        noWrap={optionDisplay === "truncate"}
        sx={{
          ...(adornment ? { flex: 1, minWidth: 0 } : { width: "100%" }),
          fontSize: "inherit",
          ...(optionDisplay === "wrap" && { overflowWrap: "anywhere" }),
          ...(optionDisplay === "clamp" && {
            display: "-webkit-box",
            WebkitLineClamp: String(optionMaxLines),
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            overflowWrap: "anywhere",
          }),
        }}
      >
        {optionLabel}
      </Typography>
    );
    return (
      <li key={optionIndex ?? optionLabel} {...optionProps}>
        {adornment ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
            }}
          >
            {text}
            {adornment}
          </Box>
        ) : (
          text
        )}
      </li>
    );
  };

  // MUI pins the popper to the anchor's width inline, so the menu can only be
  // wider if that inline width is released and the paper sizes to its content
  // between bounds. Releasing it keeps popper.js's `preventOverflow` and `flip`
  // honest, because the popper box then shrink-wraps what is actually rendered.
  const FlexiblePopper = React.useCallback(
    (popperProps: PopperProps) => {
      // The floor is never below the field itself. A flat floor made the menu
      // NARROWER than its input on wide fields — a full-width field in the 734px
      // dialog would have dropped from ~670px to the cap. Read the same way MUI
      // reads it, at render, from the same anchor.
      const anchorWidth =
        (popperProps.anchorEl as HTMLElement | null)?.clientWidth ?? 0;
      return (
        <Popper
          {...popperProps}
          placement="bottom-start"
          style={{ ...popperProps.style, width: "auto" }}
          sx={{
            "& .MuiAutocomplete-paper": {
              width: "max-content",
              minWidth: Math.max(dropdownMinWidth, anchorWidth),
              // `preventOverflow` clamps POSITION, never width, so without the
              // viewport term a 456px menu renders 456px wide on a 390px phone
              // and the overhang is unreachable — `index.css` sets
              // `html, body { overflow: hidden }`, so there is no scroll to it.
              maxWidth: `min(${dropdownMaxWidth}px, calc(100vw - 2rem))`,
            },
          }}
        />
      );
    },
    [dropdownMinWidth, dropdownMaxWidth]
  );

  // Two rendering modes cannot take the flexible menu, and both are hard blocks
  // here rather than a caller's choice to get wrong.
  //
  // `virtualize`: react-window gives every row `position: absolute; width: 100%`
  // and its list `width: 100%`, and neither contributes to intrinsic sizing — so
  // `width: max-content` resolves to zero content and the menu pins to the floor
  // forever. Measured: 248px for virtualized rows against 456px for in-flow ones,
  // which on the one virtualized field in the app (a ~418px country filter in a
  // 450px drawer) would be a shrink, not a widening.
  //
  // `isCustomList`: those listboxes discard `renderOption` and draw their own
  // rows out of fixed-width buttons and thumbnails, so a content-sized menu would
  // be driven by that chrome rather than by any label and would sit at the ceiling
  // permanently. This used to be pinned at the two call sites instead, where a
  // third one — or a direct `<CustomAutocomplete isCustomList customList={...} />`
  // — would have missed it.
  const effectiveDropdownWidth =
    virtualize || isCustomList ? "input" : dropdownWidth;

  const renderBoundedTags = (
    tagValues: any[],
    getTagProps: AutocompleteRenderGetTagProps
  ) => {
    const shown =
      collapseTagsAt === null ? tagValues : tagValues.slice(0, collapseTagsAt);
    const hidden =
      collapseTagsAt === null ? [] : tagValues.slice(collapseTagsAt);
    const chips = shown.map((option: any, index: number) => {
      const tagLabel = getLabel(option);
      const { key, ...tagProps } = getTagProps({
        index,
      }) as ReturnType<AutocompleteRenderGetTagProps> & { key?: string };
      return (
        // No `size` — this override exists only to bound the label width, so it
        // must leave MUI's default chip sizing alone.
        <Chip
          {...tagProps}
          {...(getTagChipProps?.(option) ?? {})}
          key={key ?? tagLabel}
          label={tagLabel}
          sx={{ maxWidth: "220px" }}
        />
      );
    });
    if (hidden.length === 0) return chips;
    // The same "+N" MUI renders for `limitTags`, plus a tooltip naming what it
    // hides. Each hidden value can still be removed from the menu, where it is
    // shown selected.
    const hiddenLabels = hidden.map(getLabel);
    const tooltipLabels =
      hiddenLabels.length > HIDDEN_TAGS_TOOLTIP_MAX
        ? [
            ...hiddenLabels.slice(0, HIDDEN_TAGS_TOOLTIP_MAX),
            `and ${hiddenLabels.length - HIDDEN_TAGS_TOOLTIP_MAX} more`,
          ]
        : hiddenLabels;
    return [
      ...chips,
      <Tooltip
        key="hidden-tags"
        placement="top"
        title={
          <Box component="span" sx={{ whiteSpace: "pre-line" }}>
            {tooltipLabels.join("\n")}
          </Box>
        }
      >
        <span className={autocompleteClasses.tag}>+{hidden.length}</span>
      </Tooltip>,
    ];
  };

  return (
    <>
      <Autocomplete
        multiple={isMultiple}
        disableCloseOnSelect={isMultiple}
        freeSolo={allowCustomValues}
        loading={loading}
        options={valueContainsLabel ? options : values}
        filterOptions={
          disableClientFilter
            ? (opts) => opts
            : customMatcher
            ? filterOptions
            : createFilterOptions()
        }
        getOptionLabel={getLabel}
        getOptionDisabled={(option) => disabledValues.includes(option)}
        popupIcon={<ExpandMoreOutlinedIcon />}
        onInput={handleOnInputChange}
        readOnly={readOnly}
        renderInput={(params) => {
          // InputLabelProps is meant for a label we don't render; left in `rest`
          // it reaches the DOM as inputlabelprops="[object Object]".
          const { InputProps, inputProps, InputLabelProps, ...rest } = params;
          return (
            <CustomFormControl
              label={label}
              required={required}
              error={error}
              toolTip={toolTip}
              disabled={disabled}
              extraButtons={extraLabelButtons}
            >
              <OutlinedInput
                {...rest}
                {...InputProps}
                placeholder={placeholder}
                type={inputType}
                endAdornment={
                  <React.Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {InputProps.endAdornment}
                  </React.Fragment>
                }
                inputProps={{
                  ...inputProps,
                  ...dynamicInputProps(inputProps),
                }}
              />
            </CustomFormControl>
          );
        }}
        value={autocompleteValue({
          value: field.value,
          isMultiple,
          valueContainsLabel,
        })}
        onChange={handleOnChange}
        disabled={disabled}
        disableClearable={disableClearable}
        {...(!virtualize && !isCustomList
          ? { renderOption: renderOptionLabel }
          : {})}
        {...(isMultiple ? { renderTags: renderBoundedTags } : {})}
        {...(effectiveDropdownWidth === "flexible"
          ? { PopperComponent: FlexiblePopper }
          : {})}
        sx={{
          ".MuiAutocomplete-input": {
            padding: "0 !important",
            // MUI ships `text-overflow: ellipsis` here, but the computed style is
            // `overflow: clip` + `white-space: normal`, so it never renders and the
            // value is hard-clipped mid-word instead.
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            ...secondaryStyles,
          },
          ".MuiOutlinedInput-root": {
            padding: "0 0 0 20px",
            ...secondaryStyles,
          },
        }}
        {...dynamicProps}
      />
    </>
  );
}

export type TCustomAutocomplete = {
  label?: string | null;
  type?: InputFieldType;
  itemList?: any[];
  required?: boolean;
  error?: formFieldError;
  placeholder?: string | undefined;
  disabled?: boolean;
  valueContainsLabel?: boolean;
  isMultiple?: boolean;
  loading?: boolean;
  onSearch?: any;
  onChange?: (value: any, reason: AutocompleteChangeReason) => void;
  readOnly?: boolean;
  disabledValues?: string[];
  allowCustomValues?: boolean;
  inputType?: string;
  toolTip?: string | null;
  customMatcher?: boolean;
  virtualize?: boolean;
  disableClearable?: boolean;
  /**
   * Chips to render before the rest collapse into a "+N" summary, while the
   * field is not focused. Multi-select only; unset means MUI's default of
   * showing every chip.
   *
   * Worth setting on any multi-select narrower than its content. In a ~240px
   * filter-bar cell the input's `flex-grow: 1` claims the rest of the first line,
   * so each chip after the first wraps onto its own — four selections made one
   * field 194px tall against 80px for its neighbours, and the row it sat in grew
   * to match. Focusing still expands to the full set, which is when you want it,
   * unless `keepTagsCollapsed` is set.
   */
  limitTags?: number;
  /**
   * Keep the `limitTags` collapse while the field has focus too, so its height
   * does not change on focus or blur. Multi-select with `limitTags` only; off by
   * default.
   *
   * MUI expands the chips on focus and collapses them on blur. In a dense
   * filter grid the blur fires on mousedown, everything below the field moves
   * up before mouseup, and the click lands on whatever slid under the pointer
   * instead of the control it was aimed at. With this on, the field shows the
   * same chips and "+N" in both states. The "+N" carries a tooltip naming the
   * hidden values, and each one stays selectable — and removable — in the menu.
   */
  keepTagsCollapsed?: boolean;
  stringToRenderBeforeValue?: string;
  noMenuAllowed?: boolean;
  customIsOptionEqualToValue?: (option: any, value: any) => boolean;
  isCustomList?: boolean;
  renderTags?: (
    value: any[],
    getTagProps: AutocompleteRenderGetTagProps,
    ownerState: AutocompleteOwnerState<any, boolean, boolean, boolean, "div">
  ) => React.ReactNode;
  /**
   * Something to show at the end of an option's row (a status pill), or null.
   * Gets the option as the Autocomplete holds it — the bare value unless
   * `valueContainsLabel`. Ignored with `virtualize` / `isCustomList`, which
   * draw their own rows.
   */
  renderOptionAdornment?: (option: any) => React.ReactNode;
  /**
   * Per-chip styling for a multi-select (an icon, a colour, a tooltip), or
   * null for the default chip. Same option shape as `renderOptionAdornment`.
   * Label, key, delete handling and the width bound stay the component's.
   * Ignored when a caller passes its own `renderTags`.
   */
  getTagChipProps?: (
    option: any
  ) => Pick<ChipProps, "icon" | "color" | "variant" | "title"> | null;
  listboxProps?: any;
  extraLabelButtons?: ExtraButtonsWithInputLabel[];
  isWatch?: boolean;
  customList?: any;
  isRetainInputValue?: boolean;
  disableClientFilter?: boolean;
  /**
   * How an option too long for the menu is handled.
   *
   * - `truncate` — one line, ellipsis, full value via the app-level
   *   `<OverflowTooltip />`. What every dropdown did before the house default
   *   changed.
   * - `wrap` — the option runs onto as many lines as it needs. Nothing is
   *   hidden, but option heights stop being uniform, which is what makes a long
   *   list harder to scan.
   * - `clamp` — wraps, but never past `optionMaxLines` (four by default). Bounds
   *   the row height the way the table's Full view does. This is the default.
   */
  optionDisplay?: "truncate" | "wrap" | "clamp";
  /** Lines a `clamp` option may run to before it is cut. Overrides the default. */
  optionMaxLines?: number;
  /**
   * `input` — the menu is exactly as wide as the field, which is MUI's default
   * and why a narrow filter truncates so hard.
   * `flexible` — the menu sizes to its content between a floor and a ceiling,
   * so short lists stay tight and long values get room before anything is cut.
   * Bounds follow GitLab's dropdown guidance (15.5rem / 28.5rem).
   */
  dropdownWidth?: "input" | "flexible";
  /**
   * Bounds for the `flexible` menu, in px. Per field, so a cramped filter can be
   * held narrower and a roomy form field let out, without moving anything else.
   * The floor is raised to the field's own width automatically — these only ever
   * widen a menu, never make it narrower than its input.
   */
  dropdownMinWidth?: number;
  dropdownMaxWidth?: number;
};

type CustomAutocompleteProps<T extends FieldValues = FieldValues> =
  UseControllerProps<T> & TCustomAutocomplete;
